/**
 * Resending the verification link, and the one-time address correction
 * (RidgeWill/account#112, over RidgeWill/auth#587).
 *
 * Since auth#587 an account can be signed in before its address is proven.
 * Until it is, the service refuses the changes that would let whoever
 * registered an address they do not own plant something that survives the
 * real owner taking the account back: a passkey, an authenticator app, a
 * recovery address and the normal email change. This file
 * is the portal's half of getting out of that state.
 *
 * ── EVERY SENTENCE IS KEYED ON THE CODE, NOT THE SERVER'S WORDING ────
 *
 * The service writes a good message for most of these, and `call()` keeps it.
 * They are restated here anyway because the screen BRANCHES on several of them
 * (mismatch offers the correction, already-verified refetches the session), and
 * a branch keyed on a sentence breaks the day somebody rewords it. The code is
 * the interface; see `ApiError.code`.
 */
import { createContext, useContext } from 'react';
import {
  ApiError,
  correctUnverifiedEmail,
  getAccountCaptcha,
  resendVerificationLink,
  type CaptchaConfig,
} from './api.js';

/** Where somebody goes when the self-service route has run out. */
export const SUPPORT_ADDRESS = 'support@ridgewill.com';

/** How long a verification link works (auth#587, `LINK_LIFETIME_SECONDS`). */
export const LINK_LIFETIME_TEXT = '48 hours';

/**
 * The one line every locked control shows instead of itself.
 *
 * ONE sentence for all five, so the screens cannot drift into five
 * explanations of one rule. Hidden-with-a-reason rather than disabled-silently:
 * a greyed button with no word beside it reads as the portal being broken.
 */
export const VERIFY_FIRST = 'Verify your email to set this up.';

/**
 * Is this account's address proven?
 *
 * ── DEFAULTS TO TRUE, AND THAT IS THE SAFE DIRECTION HERE ────────────
 *
 * A screen rendered outside the provider (every existing test, and any future
 * screen nobody wired up) behaves as it always has. The server is still the
 * one that refuses, so a wrong `true` costs one refusal with the server's own
 * sentence, while a wrong `false` would hide features from every verified
 * account.
 */
export const EmailVerifiedContext = createContext<boolean>(true);

export const useEmailVerified = () => useContext(EmailVerifiedContext);

/**
 * Read off the session user. Only an explicit `false` counts as unverified.
 *
 * `undefined` is an older auth deployment that does not send the field, and
 * treating that as "unverified" would lock every account on the day this ships
 * ahead of the service, which this app has done before (see
 * `RecoveryAddressResponse`).
 */
export function isUnverified(user: { emailVerified?: boolean | null } | null | undefined): boolean {
  return user?.emailVerified === false;
}

// ── CAPTCHA ─────────────────────────────────────────────────────────────

/**
 * The account deployment's captcha, or `null` when it runs none.
 *
 * A failed lookup is treated as "off", the same call the login app makes in
 * `use-captcha.ts`: if the server does want a token it refuses the submit with
 * `MISSING_RESPONSE`, and `refusalText` says so. Guessing "on" instead would
 * block the form behind a widget that may not be configured.
 */
export async function loadCaptcha(): Promise<CaptchaConfig | null> {
  try {
    return await getAccountCaptcha();
  } catch {
    return null;
  }
}

// ── OUTCOMES ────────────────────────────────────────────────────────────

export type ResendOutcome =
  | { ok: true }
  | { ok: false; code?: string; message: string; correctionAvailable?: boolean };

export type CorrectOutcome =
  | { ok: true; email: string }
  | { ok: false; code?: string; message: string };

export async function resendLink(email: string, captchaToken: string | null): Promise<ResendOutcome> {
  try {
    await resendVerificationLink(email.trim(), captchaToken);
    return { ok: true };
  } catch (err) {
    const code = err instanceof ApiError ? err.code : undefined;
    const correctionAvailable =
      code === 'EMAIL_MISMATCH' && err instanceof ApiError
        ? (err.body as { correctionAvailable?: unknown } | undefined)?.correctionAvailable === true
        : undefined;
    return { ok: false, code, message: refusalText(err, { correctionAvailable }), correctionAvailable };
  }
}

export async function correctEmail(
  email: string,
  confirmEmail: string,
  captchaToken: string | null,
): Promise<CorrectOutcome> {
  try {
    const out = await correctUnverifiedEmail(email.trim(), confirmEmail.trim(), captchaToken);
    return { ok: true, email: out.email ?? email.trim() };
  } catch (err) {
    return { ok: false, code: err instanceof ApiError ? err.code : undefined, message: refusalText(err) };
  }
}

/**
 * One plain sentence per refusal.
 *
 * Pure and exported so the mapping has a test of its own rather than being
 * reached only through a rendered form.
 */
export function refusalText(err: unknown, opts: { correctionAvailable?: boolean } = {}): string {
  if (!(err instanceof ApiError)) return 'We could not reach the server. Check your connection and try again.';

  // Before the codes: Better Auth's rate limiter answers 429 with no code of ours.
  if (err.status === 429) {
    return 'You have tried this too many times. Wait an hour, then try again.';
  }

  switch (err.code) {
    case 'EMAIL_MISMATCH':
      return opts.correctionAvailable
        ? "That isn't the address on your account."
        : `That isn't the address on your account. If you can't get into the address it was set up with, contact ${SUPPORT_ADDRESS}.`;
    case 'EMAIL_ALREADY_VERIFIED':
      return 'Your email address is already verified. Everything is unlocked.';
    case 'EMAIL_CONFIRMATION_MISMATCH':
      return "The two addresses don't match.";
    case 'EMAIL_UNCHANGED':
      return "That's the address already on your account. Use Resend link instead.";
    case 'INVALID_EMAIL':
      return "That email address can't be used.";
    case 'EMAIL_CORRECTION_USED':
      return `You have already corrected your email address once. Verify the new one, or contact ${SUPPORT_ADDRESS}.`;
    case 'EMAIL_REGISTRATION_BLOCKED':
      return `That email address can't be used. If you think that's a mistake, contact ${SUPPORT_ADDRESS}.`;
    // Better Auth's captcha plugin. Its own sentences name the mechanism and
    // not what to do about it.
    case 'MISSING_RESPONSE':
    case 'VERIFICATION_FAILED':
      return 'The security check did not go through. Complete it again, then resubmit.';
  }

  /**
   * A 404 is the account deployment's allow-list, not the endpoint: a path it
   * does not serve answers before anything runs. Said plainly, because the
   * person cannot fix it and the server's "Not Found" would read as a typo
   * on their part.
   */
  if (err.status === 404) return 'This is not available right now. Try again later.';

  return err.message || 'Something went wrong. Try again in a moment.';
}
